"use client";

import {
  Briefcase,
  Clock,
  EyeOff,
  Globe,
  Smartphone,
  Users,
} from "lucide-react";
import Link from "next/link";

import {
  formatCurrency,
  formatDuration,
  formatRelativeTime,
} from "@/lib/format";
import { cn } from "@/lib/utils";

import { successRateGrade } from "@/features/jobs/components/job-success-rate";
import type { JobSummary } from "@/features/jobs/types";

/** Link target for a job — labelling jobs (automation jobs with no automation
 * id) go straight to the labelling workspace in the legacy app. */
export function jobHref(
  job: Pick<JobSummary, "jobId" | "jobType" | "automationId">,
) {
  if (job.jobType === "devices_automation" && !job.automationId) {
    return `/labelling?job_id=${job.jobId}`;
  }
  return `/job_details?job_id=${job.jobId}`;
}

const FOCUS =
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-card";

/**
 * Success-rate grade → colour, pinned for the dark card surface.
 *
 * The card canvas is pinned dark, so the shared `JobSuccessRate` (which follows
 * the theme's `--rate-*` ramp) can't be used directly here. These hexes were
 * verified against the card surface — dropping the tinted gradient for the flat
 * `bg-card` only darkened the backdrop, so every grade clears its previous
 * ratio. Thresholds still come from `successRateGrade`, so grading can't drift
 * from the shared logic.
 */
const RATE_COLOR: Record<ReturnType<typeof successRateGrade>, string> = {
  high: "hsl(var(--rate-high))",
  mid: "hsl(var(--rate-mid))",
  low: "hsl(var(--rate-low))",
  poor: "hsl(var(--rate-poor))",
};

/** A labelled metric in the card's stat rail — a small caps label stacked over
 * a tabular value, the market-card rhythm used by Runpod / Polymarket. */
function StatBlock({
  label,
  children,
  className,
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("min-w-0", className)}>
      <div className="text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
        {label}
      </div>
      <div className="mt-1 flex items-center gap-1.5 text-sm tabular-nums text-foreground">
        {children}
      </div>
    </div>
  );
}

/**
 * A single job in the `/jobs` grid — the locked "Gradient card" (Railway)
 * layout, flattened to the HighBid house treatment: a plain `bg-card` surface
 * with a hairline border, a category glyph, an eyebrow + title, then a labelled
 * stat rail that leads with the reward and a thin completion bar. The whole card
 * is a single stretched link (the title carries the semantics); the hide control
 * sits above it in the top-right.
 *
 * The glow is deliberately gone (HighBid: "this page is glowing too much"). The
 * Iris accent is now load-bearing only — the category glyph, the completion
 * bar, the hover border and the focus ring — never ambient on the frame.
 *
 * Field set mirrors legacy `/jobs` exactly: category · price · duration · slots
 * · success rate · posted — no more, no less.
 */
export function JobCard({
  job,
  onHide,
}: {
  job: JobSummary;
  onHide?: () => void;
}) {
  const href = jobHref(job);
  const completed = job.totalTasks - job.pendingTasks;
  const positionsLabel = job.positions === 0 ? "∞" : job.positions;
  const slots = `${job.jobDone} / ${positionsLabel}`;
  const isDevice = job.jobType === "devices_automation";

  // Category glyph, matching legacy: device-automation jobs read with a phone,
  // crowd tasks with a briefcase (legacy used device_automation.png vs
  // brifcase.png). The eyebrow surfaces the same category as text.
  const category = isDevice
    ? { label: "Device automation", Icon: Smartphone }
    : { label: "Crowd task", Icon: Briefcase };

  // Completion bar under the stats, mirroring legacy's job_done/positions
  // progress. Unbounded jobs (positions 0) have no meaningful completion, so
  // the track sits empty — exactly as legacy renders it.
  const completionPct =
    job.positions === 0
      ? 0
      : Math.min(100, Math.round((job.jobDone / job.positions) * 100));

  const rate = completed > 0 ? (job.satisfiedTasks / completed) * 100 : null;

  return (
    <article
      className={cn(
        "group relative flex h-full flex-col rounded-2xl border border-border bg-card p-5",
        // Flat by request (HighBid: "this page is glowing too much"). The card
        // is a plain surface with a hairline edge — no ambient shadow, no
        // tinted gradient, no lit top. Hover brightens the border to the Iris
        // accent, which is the only decorative colour left on the frame.
        "transition-colors duration-200 hover:border-primary/50",
      )}
    >
      {/* Hide control — pinned cleanly in the top-right corner, above the
       * stretched title link so it stays independently clickable. */}
      {onHide ? (
        <button
          type="button"
          onClick={onHide}
          aria-label={`Hide job: ${job.title}`}
          className={cn(
            "absolute right-3.5 top-3.5 z-10 grid size-8 place-items-center rounded-lg text-muted-foreground transition-colors hover:bg-accent hover:text-foreground",
            FOCUS,
          )}
        >
          <EyeOff aria-hidden="true" className="size-4" />
        </button>
      ) : null}

      {/* Header — glyph + eyebrow/title. */}
      <div className="relative flex items-start gap-3.5 pr-9">
        <span
          aria-hidden="true"
          className="grid size-11 shrink-0 place-items-center rounded-xl bg-primary/10 text-primary-text ring-1 ring-inset ring-primary/25"
        >
          <category.Icon className="size-5" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
            {category.label}
          </p>
          <Link
            href={href}
            className={cn(
              "mt-0.5 block text-[15px] font-semibold leading-snug text-foreground outline-none after:absolute after:inset-0 group-hover:text-primary-text",
              FOCUS,
            )}
          >
            <span className="line-clamp-2">{job.title}</span>
          </Link>
        </div>
      </div>

      {/* Meta line — worldwide reach + posting age, quiet supporting text. */}
      {job.worldwide || job.added ? (
        <div className="relative mt-2.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
          {job.worldwide ? (
            <span className="inline-flex items-center gap-1">
              <Globe aria-hidden="true" className="size-3.5" />
              Worldwide
            </span>
          ) : null}
          {job.worldwide && job.added ? (
            <span aria-hidden="true" className="text-muted-foreground">
              ·
            </span>
          ) : null}
          {job.added ? (
            <span>Posted {formatRelativeTime(job.added)}</span>
          ) : null}
        </div>
      ) : null}

      {/* Stat rail — reward leads, then duration + success. mt-auto pushes the
       * whole block to the card foot so cards of differing title height keep
       * their metrics aligned across the grid. */}
      <div className="relative mt-auto pt-5">
        <div className="grid grid-cols-[auto,1fr,auto] items-end gap-3 border-t border-hairline pt-4">
          <StatBlock label="Reward">
            <span className="text-lg font-bold leading-none text-foreground">
              {formatCurrency(job.price)}
            </span>
          </StatBlock>
          <StatBlock label="Duration">
            <Clock aria-hidden="true" className="size-3.5 text-muted-foreground" />
            <span className="truncate">{formatDuration(job.duration)}</span>
          </StatBlock>
          <StatBlock label="Success" className="text-right">
            <span className="w-full">
              {rate === null ? (
                <span className="text-muted-foreground">—</span>
              ) : (
                <span
                  className="font-semibold tabular-nums"
                  style={{ color: RATE_COLOR[successRateGrade(rate)] }}
                >
                  {rate.toFixed(0)}%
                </span>
              )}
            </span>
          </StatBlock>
        </div>

        {/* Slots + completion bar — job_done / positions. */}
        <div className="mt-4">
          <div className="flex items-center justify-between text-[11px] text-muted-foreground">
            <span className="inline-flex items-center gap-1.5">
              <Users aria-hidden="true" className="size-3.5 text-muted-foreground" />
              <span className="tabular-nums text-foreground">{slots}</span>
              <span>slots filled</span>
            </span>
            {job.positions !== 0 ? (
              <span className="tabular-nums text-muted-foreground">
                {completionPct}%
              </span>
            ) : null}
          </div>
          <div
            className="mt-2 h-1.5 overflow-hidden rounded-full bg-hairline"
            role="progressbar"
            aria-label="Positions filled"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={completionPct}
          >
            <div
              className="h-full rounded-full bg-primary transition-[width] duration-500"
              style={{ width: `${completionPct}%` }}
            />
          </div>
        </div>
      </div>
    </article>
  );
}
